<script setup>
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import InputText from '../../../../../../../resources/js/components/umisis/form/inputs/InputText.vue'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { useApiAction } from '../../../../../../../resources/js/composables/dispatchers/useApiAction.js'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import { useFaqStore } from '../../stores/faqs.js'

const props = defineProps({
  businessSystemId: {
    type: [Number, String],
    required: true,
  },
})

const emit = defineEmits(['saved'])
const { componentRef: dialog, openModal, closeModal } = useModalControl()
const faqStore = useFaqStore()

const newQuestion = ref('')
const newAnswer = ref('')
const editingFaq = ref(null)
const normalizedBusinessSystemId = computed(() => Number(props.businessSystemId))
const isValidBusinessSystemId = computed(() => Number.isInteger(normalizedBusinessSystemId.value) && normalizedBusinessSystemId.value > 0)
const isSubmitDisabled = computed(() => loading.value || !isValidBusinessSystemId.value)
const isEditing = computed(() => Number.isInteger(Number(editingFaq.value?.id)) && Number(editingFaq.value?.id) > 0)
const modalTitle = computed(() => isEditing.value ? 'Edit FAQ' : 'Create FAQ')
const modalDescription = computed(() => {
  return isEditing.value
    ? 'Update this FAQ and publish it as a new version.'
    : 'Add a frequently asked question for this business system.'
})
const okTitle = computed(() => isEditing.value ? 'Publish' : 'Create')

const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
const fieldControlClass = (fieldName) => ([
  fieldControlBaseClass,
  validationResult(fieldName) === false ? 'border-destructive focus-visible:ring-destructive bg-red-50' : '',
  validationResult(fieldName) === true ? 'border-success focus-visible:ring-success bg-green-50' : '',
])

const resetFields = () => {
  if (isEditing.value) {
    newQuestion.value = String(editingFaq.value?.question ?? '')
    newAnswer.value = String(editingFaq.value?.answer_content_text ?? '')

    return
  }

  newQuestion.value = ''
  newAnswer.value = ''
}

const { execute, loading, getError, validationResult } = useApiAction((payload) => {
  if (isEditing.value) {
    return faqStore.publishFaqVersion(editingFaq.value, payload)
  }

  return faqStore.createFaq(payload)
}, {
  onSuccess: (faq) => {
    toast.success(isEditing.value ? 'FAQ published successfully.' : 'FAQ created successfully.')
    emit('saved', faq)
    closeModal()
  },
})

const openCreateModal = () => {
  editingFaq.value = null
  openModal()
}

const openEditModal = (faq) => {
  editingFaq.value = faq
  openModal()
}

const handleHide = () => {
  editingFaq.value = null
}

defineExpose({
  openModal: openCreateModal,
  openCreateModal,
  openEditModal,
  closeModal,
})

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  if (!isValidBusinessSystemId.value) {
    toast.error('Invalid business system id.')
    return
  }

  await execute({
    question: newQuestion.value,
    answer_content_text: newAnswer.value,
    ...(isEditing.value
      ? {}
      : {
          business_system_id: normalizedBusinessSystemId.value,
        }),
  })
}
</script>

<template>
  <UmisisModal
    ref="dialog"
    :title="modalTitle"
    :description="modalDescription"
    :ok-title="okTitle"
    cancel-title="Cancel"
    :ok-disabled="isSubmitDisabled"
    @ok="handleOk"
    @show="resetFields"
    @hide="handleHide"
  >
    <div class="flex flex-col gap-4">
      <InputText
        v-model="newQuestion"
        label="Question"
        placeholder="How often do we review this process?"
        :validation="validationResult('question')"
        :error-message="getError('question')"
      />

      <Field>
        <FieldLabel for="faq-answer">
          Answer
        </FieldLabel>
        <textarea
          id="faq-answer"
          v-model="newAnswer"
          rows="4"
          placeholder="Provide a clear answer."
          :class="fieldControlClass('answer_content_text')"
        />
        <FieldDescription v-if="getError('answer_content_text')" class="text-destructive font-medium">
          {{ getError('answer_content_text') }}
        </FieldDescription>
        <FieldDescription v-else-if="getError('answer_content')" class="text-destructive font-medium">
          {{ getError('answer_content') }}
        </FieldDescription>
      </Field>
    </div>
  </UmisisModal>
</template>
